/**
 * ViewModel: Programs
 * Presentation logic for programs feature.
 */

import type { Dispatch, SetStateAction } from "react";
import type {
  FetchProgramsParams,
  ProgramItem,
  ProgramsPaging,
} from "../../domain/model/ProgramsModel";
import { FetchProgramsPaginatedUseCase } from "../../domain/usecase/FetchProgramsUseCase";
import { DeleteProgramUseCase } from "../../domain/usecase/ProgramUseCases";

export interface ProgramsViewModelParams {
  setItems: Dispatch<SetStateAction<ProgramItem[]>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setPaging: Dispatch<SetStateAction<ProgramsPaging | null>>;
  setError: Dispatch<SetStateAction<string | null>>;
}

export const ProgramsViewModel = ({
  setItems,
  setIsLoading,
  setPaging,
  setError,
}: ProgramsViewModelParams) => {
  const loadPrograms = async (params: FetchProgramsParams = {}) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await FetchProgramsPaginatedUseCase(params);

      setItems(result.items.slice());
      setPaging(result.paging);
    } catch (error) {
      console.error("Failed to load programs:", error);
      setItems([]);
      setPaging(null);
      setError("Failed to load programs. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProgram = async (programId: string): Promise<boolean> => {
    try {
      await DeleteProgramUseCase(programId);
      return true;
    } catch (error) {
      console.error("Failed to delete program:", error);
      setError("Failed to delete program. Please try again.");
      return false;
    }
  };

  return {
    loadPrograms,
    deleteProgram,
  };
};
